{
    This file is part of the Fresnel Library.
    Copyright (c) 2026 by the FPC & Lazarus teams.

    Observer for element resizing and device pixel ratio changes

    See the file COPYING.modifiedLGPL.txt, included in this distribution,
    for details about the copyright.

    This program is distributed in the hope that it will be useful,
    but WITHOUT ANY WARRANTY; without even the implied warranty of
    MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

 **********************************************************************}

unit fresnel.resizepixelratio.observer.pas2js;

{$mode ObjFPC}
{$h+}
{$modeswitch externalclass}
{$modeswitch advancedrecords}

interface

uses
     SysUtils
   , JS
   , Web
   ;

type

  { TResizePixelRatioEventData }

  TResizePixelRatioEventData = class external name 'Object' (TJSObject)
    Element : TJSHTMLElement;       // The observed element
    EventType : String;             // Either 'resize' or 'pixelRatioChange'
    Entries : TJSHTMLResizeObserverEntryArray; // ResizeObserver entries (only for resize events)
    PixelRatio : Double;           // Current devicePixelRatio value
  end;

  TResizePixelRatioCallback = reference to procedure(const aData: TResizePixelRatioEventData);

  { TResizePixelRatioObserver }

  TResizePixelRatioObserver = class external name 'Object' (TJSObject)
    procedure Disconnect; external name 'disconnect';
  end;

// Creates an observer for element resizing and pixel ratio changes
function CreateResizePixelRatioObserver(
  aElement : TJSHTMLElement;
  aCallback : TResizePixelRatioCallback
  ) : TResizePixelRatioObserver;

// --------------------------------------------------------------------
// --------------------------------------------------------------------
// --------------------------------------------------------------------
implementation
// --------------------------------------------------------------------
// --------------------------------------------------------------------
// --------------------------------------------------------------------

// CreateResizePixelRatioObserver
//
function CreateResizePixelRatioObserver(
  aElement : TJSHTMLElement;
  aCallback : TResizePixelRatioCallback
  ) : TResizePixelRatioObserver;
var
  lCurrentPixelRatio : Double;
  lResizeObserver : TJSHTMLResizeObserver;
  lMediaQueryList : TJSMediaQueryList;
  lHandlePixelRatioChange : TJSRawEventHandler;
  lObserver : TJSObject;
begin
  lCurrentPixelRatio := Window.devicePixelRatio;

  // Create resize observer for the element
  lResizeObserver := TJSHTMLResizeObserver.new(
    procedure (aEntries : TJSHTMLResizeObserverEntryArray; aObserver : TJSHTMLResizeObserver)
    var
      lData: TResizePixelRatioEventData;
    begin
      lData := TResizePixelRatioEventData.new;
      lData.element := aElement;
      lData.eventType := 'resize';
      lData.entries := aEntries;
      lData.pixelRatio := Window.devicePixelRatio;
      aCallback(lData);
    end
  );

  lResizeObserver.observe(aElement);

  // Create media query to detect devicePixelRatio changes
  lMediaQueryList := Window.matchMedia('(resolution: ' + FloatToStr(lCurrentPixelRatio) + 'dppx)');

  lHandlePixelRatioChange :=
    procedure (aEvent: TJSEvent)
    var
      lData: TResizePixelRatioEventData;
    begin
      if Window.devicePixelRatio <> lCurrentPixelRatio then
      begin
        lCurrentPixelRatio := Window.devicePixelRatio;

        lData := TResizePixelRatioEventData.new;
        lData.element := aElement;
        lData.eventType := 'pixelRatioChange';
        lData.pixelRatio := lCurrentPixelRatio;
        aCallback(lData);

        // Update media query for next change
        lMediaQueryList := Window.matchMedia('(resolution: ' + FloatToStr(lCurrentPixelRatio) + 'dppx)');
        lMediaQueryList.onchange := lHandlePixelRatioChange;
      end;
  end;

  lMediaQueryList.onchange := lHandlePixelRatioChange;

  lObserver := TJSObject.new;
  lObserver['disconnect'] :=
    procedure
    begin
      lResizeObserver.disconnect;
      lMediaQueryList.onchange := nil;
    end;

  Result := TResizePixelRatioObserver(lObserver);
end;

end.
